import { Box, Stack } from '@mui/joy'
import { useGate, useStore, useStoreMap } from 'effector-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { carLicenseeModel } from '~/entities/common/Organizations/CarLicensee'
import { usePermissions } from '~/entities/common/viewer'
import { editCarLicenseeModel } from '~/features/common/Organization/CarLicensees/EditCarLicensee'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { Container, PageContainer, PageTitle } from '~/shared/ui/Layout'
import { NotFound } from '~/shared/ui/NotFound'
import RoutesTabs from '~/shared/ui/RoutesTabs'
import { AuditsModal } from '~/widgets/common/AuditsModal'
import { useConfigTabs } from './useConfigTabs'

export function EditCarLicenseePage() {
  const { t } = useTranslation()

  const { carLicenseeId } = useParams<{ carLicenseeId: string }>()
  const configTabs = useConfigTabs()
  const { canAuditsView } = usePermissions()

  useGate(editCarLicenseeModel.Gate, {
    carLicenseeId: carLicenseeId as UniqueId,
  })

  const isLoading = useStore(carLicenseeModel.requestFx.pending)
  const name = useStoreMap(editCarLicenseeModel.$carLicensee, (carLicensee) =>
    carLicensee?.getName(),
  )

  const breadcrumbs: BreadcrumbsList = useMemo(
    () => [
      { titleI18n: 'organizations', link: '/organizations/car-licensees' },
      { titleI18n: 'licensees', link: '/organizations/car-licensees' },
      { title: name || '' },
    ],
    [name],
  )

  const error = carLicenseeModel.useCarLicenseeError(carLicenseeId as string)

  if (!carLicenseeId || error) {
    return <NotFound message={t('alert.failed to load licensee')} />
  }

  return (
    <PageContainer title={t('licensee card')}>
      <RoutesTabs
        parentPath={`/organizations/car-licensees/${carLicenseeId}`}
        config={configTabs}
        parentContent={
          <Container isHeader>
            <Stack
              direction='row'
              alignItems='end'
              justifyContent='space-between'
            >
              <Box>
                <Breadcrumbs list={breadcrumbs} lastLoading={isLoading} />
                <PageTitle loading={isLoading}>{name}</PageTitle>
              </Box>

              {canAuditsView && <AuditsModal auditableId={carLicenseeId} />}
            </Stack>
          </Container>
        }
      />
    </PageContainer>
  )
}
